import {
  getAuth,
  isSignInWithEmailLink,
  onAuthStateChanged,
  signInWithCustomToken,
  signInWithEmailLink,
  signOut,
} from 'app/services/Firebase';
import { AppUser, toAppUser } from 'app/services/Firebase.types';
import Logger from 'app/services/Logger';
import { sleep } from 'app/utils/time';

type AuthUser = ReturnType<typeof getAuth>['currentUser'];
type Listener = (user: AppUser | null) => void;
type AuthCredential = { user?: AuthUser | null };
type EmailLinkCredentialUser = {
  email: string | null;
  emailVerified?: boolean;
  reload?: () => Promise<void>;
} | null;
type EmailLinkAuthCredential = { user?: EmailLinkCredentialUser };
type ResolvedAuthUser = AppUser | EmailLinkCredentialUser;
type VerifiedUserFallback = EmailLinkCredentialUser;

const MODULE = '[AuthService]';
const AUTH_STATE_SYNC_ATTEMPTS = 5;
const AUTH_STATE_SYNC_DELAY_MS = 100;
const AUTH_STATE_READY_TIMEOUT_MS = 5000;

let currentFirebaseUser: AuthUser = null;
const listeners = new Set<Listener>();
// handle race condition where auth has not initialized before getToken is called
const ready = new Promise<void>((resolve) => {
  const fallback = setTimeout(() => resolve(), AUTH_STATE_READY_TIMEOUT_MS);
  const unsub = onAuthStateChanged(getAuth(), () => {
    resolve();
    unsub();
    clearTimeout(fallback);
  });
});

onAuthStateChanged(getAuth(), (fbUser: AuthUser) => {
  notifyListeners(fbUser);
});

function notifyListeners(fbUser: AuthUser) {
  currentFirebaseUser = fbUser;
  const appUser = fbUser ? toAppUser(fbUser) : null;
  listeners.forEach((cb) => cb(appUser));
}

function cacheCredentialUser<T extends AuthCredential>(credential: T): T {
  if (credential?.user) {
    notifyListeners(credential.user);
  }
  return credential;
}

async function waitForVerifiedCurrentUser(
  attemptsRemaining: number,
  fallbackUser: VerifiedUserFallback = null
): Promise<ResolvedAuthUser> {
  if (attemptsRemaining <= 0) {
    return fallbackUser;
  }

  await sleep(AUTH_STATE_SYNC_DELAY_MS);
  const syncedUser = currentFirebaseUser ? toAppUser(currentFirebaseUser) : null;
  if (syncedUser?.emailVerified) {
    return syncedUser;
  }

  return waitForVerifiedCurrentUser(attemptsRemaining - 1, fallbackUser);
}

const AuthService = {
  getToken: async () => {
    await ready;
    return currentFirebaseUser?.getIdToken() ?? Promise.resolve(undefined);
  },
  refreshToken: async () => {
    await ready;
    return currentFirebaseUser?.getIdToken(true) ?? Promise.resolve(undefined);
  },
  getTokenResult: () => currentFirebaseUser?.getIdTokenResult(),
  getCurrentUser: (): AppUser | null =>
    currentFirebaseUser ? toAppUser(currentFirebaseUser) : null,
  getFirebaseUser: () => currentFirebaseUser,
  signIn: async (token: string) =>
    cacheCredentialUser(await signInWithCustomToken(getAuth(), token)),
  signInWithEmailLink: async (email: string, url: string) =>
    cacheCredentialUser(await signInWithEmailLink(getAuth(), email, url)),
  getVerifiedCurrentUserAfterSync: async (): Promise<AppUser | null> => {
    const currentUser = currentFirebaseUser ? toAppUser(currentFirebaseUser) : null;
    if (currentUser?.emailVerified) {
      return currentUser;
    }

    const resolvedUser = await waitForVerifiedCurrentUser(AUTH_STATE_SYNC_ATTEMPTS);
    return resolvedUser && 'uid' in resolvedUser ? resolvedUser : null;
  },
  resolveVerifiedUserFromEmailLinkSignIn: async (
    credential: EmailLinkAuthCredential | null
  ): Promise<ResolvedAuthUser> => {
    const authServiceUser = currentFirebaseUser ? toAppUser(currentFirebaseUser) : null;
    if (authServiceUser?.emailVerified) {
      return authServiceUser;
    }

    const credentialUser = credential?.user ?? null;

    try {
      await credentialUser?.reload?.();
    } catch (error) {
      Logger.warn(`${MODULE} unable to reload sign in credential user`, { error });
    }

    if (credentialUser?.emailVerified) {
      return credentialUser;
    }

    return waitForVerifiedCurrentUser(AUTH_STATE_SYNC_ATTEMPTS, credentialUser);
  },
  isSignInWithEmailLink: (url: string) => isSignInWithEmailLink(getAuth(), url),
  signOut: () => signOut(getAuth()),
  reload: () => currentFirebaseUser?.reload(),
  subscribe: (cb: Listener) => {
    listeners.add(cb);
    try {
      cb(currentFirebaseUser ? toAppUser(currentFirebaseUser) : null);
    } catch (error) {
      listeners.delete(cb);
      throw error;
    }
    return () => {
      listeners.delete(cb);
    };
  },
};

export default AuthService;
